import { mesh } from "topojson-client";
import worldData from "world-atlas/countries-110m.json";

// Reuse the topojson-client param type so we don't need a topojson-specification import.
const topo = worldData as unknown as Parameters<typeof mesh>[0];
const DEG = Math.PI / 180;

export interface UPoint {
  ux: number;
  uy: number;
  uz: number;
}

// All country borders as polylines of unit-sphere points, using the SAME lon/lat →
// sphere convention as the dot globe (lng measured 0..2π eastward). Decimated so the
// faint background map stays cheap to redraw every frame.
export function buildBorderPolylines(stepDeg = 2): UPoint[][] {
  const lines = mesh(
    topo,
    topo.objects.countries as unknown as Parameters<typeof mesh>[1]
  ) as unknown as { coordinates: [number, number][][] };
  const polys: UPoint[][] = [];
  for (const line of lines.coordinates) {
    const pts: UPoint[] = [];
    let prevLon = NaN;
    let prevLat = NaN;
    for (let i = 0; i < line.length; i++) {
      const [lon, lat] = line[i];
      // keep endpoints; thin out dense interior vertices
      const isEnd = i === 0 || i === line.length - 1;
      if (!isEnd && Math.abs(lon - prevLon) < stepDeg && Math.abs(lat - prevLat) < stepDeg) {
        continue;
      }
      prevLon = lon;
      prevLat = lat;
      const lng = ((lon + 360) % 360) * DEG;
      const latR = lat * DEG;
      const cosLat = Math.cos(latR);
      pts.push({ ux: cosLat * Math.cos(lng), uy: Math.sin(latR), uz: cosLat * Math.sin(lng) });
    }
    if (pts.length > 1) polys.push(pts);
  }
  return polys;
}

// lon/lat (degrees) → unit-sphere point in the same convention.
export function lonLatToUnit(lon: number, lat: number): UPoint {
  const lng = ((lon + 360) % 360) * DEG;
  const latR = lat * DEG;
  const cosLat = Math.cos(latR);
  return { ux: cosLat * Math.cos(lng), uy: Math.sin(latR), uz: cosLat * Math.sin(lng) };
}
